import { useTranslation, Trans } from 'next-i18next/pages';

import { Link } from '@/components/atoms/Link';
import { Title } from '@/components/atoms/Typography';
import { Table } from '@/components/molecules/Table';
import type { ColumnType } from '@/components/molecules/Table/Table.types';
import { Modal } from '@/components/organisms/Modal';

import styles from './EulaDetailsModal.module.css';

type DataProcessingRow = {
  categories: string;
  legalBasis: string;
  purpose: string;
};

type PosthogRow = {
  cookie: string;
  purpose: string;
  retention: string;
  type: string;
};

type EulaDetailsModalProps = {
  setShowTerms: (open: boolean) => void;
  isOpen?: boolean;
};

export default function EulaDetailsModal({ setShowTerms, isOpen = false }: EulaDetailsModalProps) {
  const { t } = useTranslation('eula');

  const dataProcessingTableData = [
    {
      purpose: t('dataProcessing.rows.providingAccess.purpose'),
      categories: t('dataProcessing.rows.providingAccess.categories'),
      legalBasis: t('dataProcessing.rows.providingAccess.legalBasis'),
    },
    {
      purpose: t('dataProcessing.rows.supportingClients.purpose'),
      categories: t('dataProcessing.rows.supportingClients.categories'),
      legalBasis: t('dataProcessing.rows.supportingClients.legalBasis'),
    },
    {
      purpose: t('dataProcessing.rows.respondingContact.purpose'),
      categories: t('dataProcessing.rows.respondingContact.categories'),
      legalBasis: t('dataProcessing.rows.respondingContact.legalBasis'),
    },
    {
      purpose: t('dataProcessing.rows.operatingImprovingWebsite.purpose'),
      categories: t('dataProcessing.rows.operatingImprovingWebsite.categories'),
      legalBasis: t('dataProcessing.rows.operatingImprovingWebsite.legalBasis'),
    },
    {
      purpose: t('dataProcessing.rows.jobApplications.purpose'),
      categories: t('dataProcessing.rows.jobApplications.categories'),
      legalBasis: t('dataProcessing.rows.jobApplications.legalBasis'),
    },
    {
      purpose: t('dataProcessing.rows.businessAdministration.purpose'),
      categories: t('dataProcessing.rows.businessAdministration.categories'),
      legalBasis: t('dataProcessing.rows.businessAdministration.legalBasis'),
    },
  ];

  const dataProcessingColumns = [
    {
      title: t('dataProcessing.columns.purpose'),
      dataIndex: 'purpose',
      key: 'purpose',
    },
    {
      title: t('dataProcessing.columns.categories'),
      dataIndex: 'categories',
      key: 'categories',
    },
    {
      title: t('dataProcessing.columns.legalBasis'),
      dataIndex: 'legalBasis',
      key: 'legalBasis',
    },
  ] satisfies ColumnType<DataProcessingRow>[];

  const posthogTableData = [
    {
      cookie: t('cookiesUsed.table.rows.posthog.cookie'),
      purpose: t('cookiesUsed.table.rows.posthog.purpose'),
      type: t('cookiesUsed.table.rows.posthog.type'),
      retention: t('cookiesUsed.table.rows.posthog.retention'),
    },
  ];

  const posthogColumns = [
    {
      title: t('cookiesUsed.table.columns.cookie'),
      dataIndex: 'cookie',
      key: 'cookie',
    },
    {
      title: t('cookiesUsed.table.columns.purpose'),
      dataIndex: 'purpose',
      key: 'purpose',
    },
    {
      title: t('cookiesUsed.table.columns.type'),
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: t('cookiesUsed.table.columns.retention'),
      dataIndex: 'retention',
      key: 'retention',
    },
  ] satisfies ColumnType<PosthogRow>[];

  const handleClose = () => {
    setShowTerms(false);
  };

  return (
    <Modal
      footerActions={{ right: [{ label: t('actions.close'), onClick: handleClose, type: 'button' }] }}
      isOpen={isOpen}
      maxWidth={1800}
      onClose={handleClose}
      title={`${t('title')} | geoFluxus`}
      titleDescription={t('description')}
    >
      <div className={styles.termsContainer}>
        {/* LEFT COLUMN */}
        <div className={styles.contentColumn}>
          <p>
            <Trans
              i18nKey="intro"
              t={t}
              components={[
                <Link
                  key="site"
                  href="https://www.geofluxus.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.text}
                />,
                <Link
                  key="platform"
                  href="https://prod-afvalprofiel.geofluxus.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.text}
                />,
              ]}
            />
          </p>

          <p>
            <Trans i18nKey="gdprCommitment" t={t} components={[<strong key="gdpr" />]} />
          </p>

          <p>{t('statementPurpose')}</p>

          <Title level={5}>{t('roles.title')}</Title>

          <p>{t('roles.intro')}</p>

          <ol>
            <li>
              <strong>{t('roles.controller.title')}</strong>
              <p>{t('roles.controller.intro')}</p>
              <ul className="eulaList">
                <li>{t('roles.controller.items.websiteVisits')}</li>
                <li>{t('roles.controller.items.contactForms')}</li>
                <li>{t('roles.controller.items.salesMarketing')}</li>
                <li>{t('roles.controller.items.jobApplications')}</li>
                <li>{t('roles.controller.items.cookiesAnalytics')}</li>
                <li>{t('roles.controller.items.businessCorrespondence')}</li>
              </ul>
            </li>

            <li>
              <strong>{t('roles.processor.title')}</strong>
              <p>
                <Trans i18nKey="roles.processor.intro" t={t} components={[<strong key="bold" />]} />
              </p>
              <p>{t('roles.processor.clientsControllers')}</p>
              <ul className="eulaList">
                <li>{t('roles.processor.items.whatDataUploaded')}</li>
                <li>{t('roles.processor.items.processingPurposes')}</li>
                <li>{t('roles.processor.items.accessRetention')}</li>
              </ul>
              <p>{t('roles.processor.dpaLine')}</p>
            </li>
          </ol>

          <Title level={5}>{t('personalData.title')}</Title>

          <ol>
            <li>
              <strong>{t('personalData.websiteContact.title')}</strong>
              <ul className="eulaList">
                <li>{t('personalData.websiteContact.items.name')}</li>
                <li>{t('personalData.websiteContact.items.email')}</li>
                <li>{t('personalData.websiteContact.items.phone')}</li>
                <li>{t('personalData.websiteContact.items.message')}</li>
              </ul>
            </li>

            <li>
              <strong>{t('personalData.platformAccounts.title')}</strong>
              <ul className="eulaList">
                <li>{t('personalData.platformAccounts.items.name')}</li>
                <li>{t('personalData.platformAccounts.items.email')}</li>
                <li>{t('personalData.platformAccounts.items.rolePermissions')}</li>
                <li>{t('personalData.platformAccounts.items.authData')}</li>
                <li>{t('personalData.platformAccounts.items.activityLogs')}</li>
              </ul>
            </li>

            <li>
              <strong>{t('personalData.uploadedClientData.title')}</strong>
              <p>{t('personalData.uploadedClientData.body')}</p>
            </li>

            <li>
              <strong>{t('personalData.jobApplicants.title')}</strong>
              <ul className="eulaList">
                <li>{t('personalData.jobApplicants.items.nameAddress')}</li>
                <li>{t('personalData.jobApplicants.items.emailPhone')}</li>
                <li>{t('personalData.jobApplicants.items.cvCoverSalary')}</li>
                <li>{t('personalData.jobApplicants.items.references')}</li>
                <li>{t('personalData.jobApplicants.items.publicInfo')}</li>
              </ul>
            </li>

            <li>
              <strong>{t('personalData.analyticsData.title')}</strong>
              <p>{t('personalData.analyticsData.intro')}</p>
              <ul className="eulaList">
                <li>{t('personalData.analyticsData.items.ip')}</li>
                <li>{t('personalData.analyticsData.items.deviceBrowser')}</li>
                <li>{t('personalData.analyticsData.items.pagesVisited')}</li>
                <li>{t('personalData.analyticsData.items.clickBehavior')}</li>
              </ul>
              <p>{t('personalData.analyticsData.noMarketingTrackers')}</p>
            </li>
          </ol>

          <Title level={5}>{t('legalBases.title')}</Title>
          <p>{t('legalBases.intro')}</p>
          <ul className="eulaList">
            <li>
              <strong>{t('legalBases.items.contract.title')}</strong>
              {t('legalBases.items.contract.body')}
            </li>
            <li>
              <strong>{t('legalBases.items.legitimateInterest.title')}</strong>
              {t('legalBases.items.legitimateInterest.body')}
            </li>
            <li>
              <strong>{t('legalBases.items.consent.title')}</strong>
              {t('legalBases.items.consent.body')}
            </li>
            <li>
              <strong>{t('legalBases.items.legalObligation.title')}</strong>
              {t('legalBases.items.legalObligation.body')}
            </li>
          </ul>

          <Title level={5}>{t('dataProcessing.title')}</Title>
          <Table
            dataSource={dataProcessingTableData}
            columns={dataProcessingColumns}
            pagination={false}
            bordered
            size="small"
          />
        </div>

        {/* RIGHT COLUMN */}
        <div className={styles.contentColumn}>
          <Title level={5}>{t('retention.title')}</Title>
          <p>{t('retention.intro')}</p>
          <ul className="eulaList">
            <li>
              <strong>{t('retention.items.platformData.title')}</strong>
              {t('retention.items.platformData.body')}
            </li>
            <li>
              <strong>{t('retention.items.websiteContact.title')}</strong>
              {t('retention.items.websiteContact.body')}
            </li>
            <li>
              <strong>{t('retention.items.jobApplications.title')}</strong>
              {t('retention.items.jobApplications.body')}
            </li>
            <li>
              <strong>{t('retention.items.analyticsData.title')}</strong>
              {t('retention.items.analyticsData.body')}
            </li>
            <li>
              <strong>{t('retention.items.adminRecords.title')}</strong>
              {t('retention.items.adminRecords.body')}
            </li>
          </ul>

          <Title level={5}>{t('sharing.title')}</Title>
          <p>{t('sharing.intro')}</p>

          <ol>
            <li>
              <strong>{t('sharing.processors.title')}</strong>
              <ul className="eulaList">
                <li>{t('sharing.processors.items.cloudHosting')}</li>
                <li>{t('sharing.processors.items.fileStorage')}</li>
                <li>{t('sharing.processors.items.emailProductivity')}</li>
                <li>{t('sharing.processors.items.analyticsProvider')}</li>
                <li>{t('sharing.processors.items.documentProcessing')}</li>
              </ul>
              <p>{t('sharing.processors.footer')}</p>
            </li>

            <li>
              <strong>{t('sharing.thirdCountry.title')}</strong>
              <p>{t('sharing.thirdCountry.intro')}</p>
              <p>{t('sharing.thirdCountry.safeguardsIntro')}</p>
              <ul className="eulaList">
                <li>{t('sharing.thirdCountry.items.sccs')}</li>
                <li>{t('sharing.thirdCountry.items.dpa')}</li>
                <li>{t('sharing.thirdCountry.items.encryption')}</li>
              </ul>
            </li>
          </ol>

          <Title level={5}>{t('cookiesSimilar.title')}</Title>
          <p>{t('cookiesSimilar.intro')}</p>

          <ol>
            <li>
              <strong>{t('cookiesUsed.title')}</strong>

              <p>
                <Trans i18nKey="cookiesUsed.posthogOnly" t={t} components={[<strong key="posthog" />]} />
              </p>

              <Table dataSource={posthogTableData} columns={posthogColumns} pagination={false} bordered size="small" />

              <p>{t('cookiesUsed.notUsedIntro')}</p>
              <ul className="eulaList">
                <li>{t('cookiesUsed.notUsed.items.marketing')}</li>
                <li>{t('cookiesUsed.notUsed.items.thirdParty')}</li>
                <li>{t('cookiesUsed.notUsed.items.socialMedia')}</li>
              </ul>
            </li>

            <li>
              <strong>{t('cookiesManaging.title')}</strong>
              <p>{t('cookiesManaging.body')}</p>
            </li>
          </ol>

          <Title level={5}>{t('rights.title')}</Title>
          <p>{t('rights.intro')}</p>
          <ul className="eulaList">
            <li>{t('rights.items.access')}</li>
            <li>{t('rights.items.rectification')}</li>
            <li>{t('rights.items.erasure')}</li>
            <li>{t('rights.items.restriction')}</li>
            <li>{t('rights.items.object')}</li>
            <li>{t('rights.items.portability')}</li>
            <li>{t('rights.items.withdraw')}</li>
          </ul>

          <p>
            <Trans
              i18nKey="rights.contact"
              t={t}
              components={[
                <Link
                  key="privacyEmail"
                  href="mailto:privacy@geofluxus.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.text}
                />,
                <strong key="oneMonth" />,
              ]}
            />
          </p>

          <Title level={5}>{t('contactInfo.title')}</Title>

          <p>
            <strong>{t('contactInfo.legalAddress.title')}</strong>
            <br />
            {t('contactInfo.legalAddress.lines.company')}
            <br />
            {t('contactInfo.legalAddress.lines.street')}
            <br />
            {t('contactInfo.legalAddress.lines.postcodeCity')}
            <br />
            {t('contactInfo.legalAddress.lines.country')}
          </p>

          <p>
            <strong>{t('contactInfo.operationalOffice.title')}</strong>
            <br />
            {t('contactInfo.operationalOffice.lines.street')}
            <br />
            {t('contactInfo.operationalOffice.lines.postcodeCity')}
            <br />
            {t('contactInfo.operationalOffice.lines.country')}
          </p>

          <p>
            <strong>{t('contactInfo.generalContact.title')}</strong>
            <br />
            <Link href="mailto:info@geofluxus.com" target="_blank" rel="noopener noreferrer" className={styles.text}>
              info@geofluxus.com
            </Link>
          </p>

          <p>
            <strong>{t('contactInfo.privacyContact.title')}</strong>
            <br />
            <Link href="mailto:privacy@geofluxus.com" target="_blank" rel="noopener noreferrer" className={styles.text}>
              privacy@geofluxus.com
            </Link>
          </p>

          <Title level={5}>{t('changes.title')}</Title>
          <p>{t('changes.body')}</p>
        </div>
      </div>
    </Modal>
  );
}
